<script setup lang="ts">
import { mdiAlert } from '@quasar/extras/mdi-v7';
import BErrorBanner from 'shared/components/base/BErrorBanner/BErrorBanner.vue';

interface Props {
  loadError?: string | null;
  validationErrors?: string[];
  /** Show "step not found" when there is neither an error nor a step. */
  isNotFound?: boolean;
}

withDefaults(defineProps<Props>(), {
  loadError: null,
  validationErrors: () => [],
  isNotFound: false,
});

const emit = defineEmits<{
  retry: [];
  back: [];
  home: [];
}>();
</script>

<template>
  <!-- Loading Error -->
  <BErrorBanner
    v-if="loadError"
    class="col-grow q-mb-auto"
    @ok="emit('retry')"
  >
    <div class="text-h6 q-mb-sm">{{ $t('STEP_LOAD_ERROR') }}</div>
    <div class="text-body2">{{ loadError }}</div>
    <template #actions>
      <q-btn
        flat
        color="white"
        :label="$t('TRY_AGAIN')"
        @click="emit('retry')"
      />
      <q-btn
        flat
        color="white"
        :label="$t('GO_BACK')"
        @click="emit('back')"
      />
    </template>
  </BErrorBanner>

  <!-- Validation Errors -->
  <q-banner
    v-else-if="validationErrors.length > 0"
    class="bg-warning text-dark q-mb-md"
    rounded
  >
    <template #avatar>
      <q-icon
        :name="mdiAlert"
        size="md"
      />
    </template>
    <div class="text-subtitle1 q-mb-sm">
      {{ $t('STEP_INCOMPLETE_DATA') }}
    </div>
    <div class="text-caption">
      <ul class="q-pl-md q-my-none">
        <li
          v-for="(error, index) in validationErrors"
          :key="index"
        >
          {{ error }}
        </li>
      </ul>
    </div>
    <template #action>
      <q-btn
        flat
        color="dark"
        :label="$t('TRY_AGAIN')"
        @click="emit('retry')"
      />
    </template>
  </q-banner>

  <!-- Step Not Found -->
  <BErrorBanner
    v-else-if="isNotFound"
    class="col-grow q-mb-auto"
    @ok="emit('home')"
  >
    {{ $t('STEP_NOT_FOUND') }}
  </BErrorBanner>
</template>
